import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Search,
  RefreshCw,
  AlertCircle,
  MapPin,
  Gauge,
  X,
  List,
  Map as MapIcon,
  Calendar,
  Truck,
  Activity,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import TripHistoryModal from "./TripHistoryModal";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import FleetMap from "./FleetMap";
import VehicleLiveMap from "./VehicleLiveMap";
import {
  fetchLiveVehicles,
  fetchLiveVehicleByNumber,
  searchLiveVehicles,
  fmtTimestamp,
  fetchMyFleetLive,
  statusLabel,
  statusTone,
} from "../../services/fleetx_service";
import "./FleetxLive.css";

const STATUS_OPTIONS = [
  "RUNNING",
  "IDLE",
  "PARKED",
  "UNREACHABLE",
  "DISCONNECTED",
  "NOPOWER",
  "BATTERY_DISCHARGED",
  "IMMOBILISED",
  "REMOVED",
];

const AUTO_REFRESH_MS = 15000;
const PAGE_SIZE = 20;

export default function FleetXLive({ apiBaseUrl, getToken }) {
  const [headerVisible, setHeaderVisible] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [liveOnly, setLiveOnly] = useState(true);
  const [limit, setLimit] = useState(500);

  const [viewMode, setViewMode] = useState("table");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [trackingVehicleNumber, setTrackingVehicleNumber] = useState(null);
  const [historyVehicleNumber, setHistoryVehicleNumber] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  const tableScrollRef = useRef(null);

  const fetchList = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        let data;
        if (searchTerm.trim()) {
          data = await searchLiveVehicles(apiBaseUrl, getToken, searchTerm.trim(), liveOnly);
          setVehicles(data.matches || []);
          setSummary(null);
        } else {
          data = await fetchMyFleetLive(apiBaseUrl, getToken, {
            liveOnly,
            light: true,
          });
          setVehicles(data.vehicles || []);
          setSummary({
            total: data.my_vehicle_count,
            matched: data.matched_count,
            missing: data.missing_count,
          });
        }

      } catch (e) {
        setError(e.message);
        setVehicles([]);
        setSummary(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiBaseUrl, getToken, searchTerm, liveOnly, limit]
  );

  useEffect(() => {
    fetchList();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => fetchList({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchList]);

  const filteredVehicles = useMemo(() => {
    if (!statusFilter) return vehicles;
    return vehicles.filter((v) => v.currentStatus === statusFilter);
  }, [vehicles, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);

    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  }, [vehicles, statusFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedVehicles = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredVehicles.slice(start, start + PAGE_SIZE);
  }, [filteredVehicles, currentPage]);

  const pageStart = filteredVehicles.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, filteredVehicles.length);

  async function openDetail(vehicleNumber) {
    setSelectedVehicle({ vehicleNumber });
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await fetchLiveVehicleByNumber(apiBaseUrl, getToken, vehicleNumber);
      setSelectedVehicle(data);
    } catch (e) {
      setDetailError(e.message);
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setSelectedVehicle(null);
    setDetailError(null);
  }

  function openTracking(vehicleNumber) {
    setTrackingVehicleNumber(vehicleNumber);
  }

  function closeTracking() {
    setTrackingVehicleNumber(null);
  }

  function openHistory(vehicleNumber) {
    setHistoryVehicleNumber(vehicleNumber);
  }

  function closeHistory() {
    setHistoryVehicleNumber(null);
  }

  return (
    <div className="fleetx-layout">
      <Sidebar />
      <div className="fleetx-content">
        <div
          className={`fleetx-header-wrapper ${
            headerVisible ? "fleetx-header-visible" : "fleetx-header-hidden"
          }`}
        >
          <Header />
        </div>

        {}
        <div className="fleetx-page-container">
          <div className="fleetx-sticky-controls">
            {summary && (
              <div className="fleetx-stats-bar">
                <StatCard label="My Vehicles" value={summary.total} icon={Truck} tone="neutral" />
                <StatCard label="Live Now" value={summary.matched} icon={Activity} tone="positive" />
                <StatCard label="No Live Data" value={summary.missing} icon={AlertCircle} tone="negative" />
              </div>
            )}

            <div className="fleetx-toolbar">
              <div className="fleetx-search-group">
                <Search size={15} className="fleetx-search-icon" />
                <input
                  type="text"
                  className="fleetx-search-input"
                  placeholder="Search by vehicle number…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchList()}
                />
                {searchTerm && (
                  <button
                    className="fleetx-clear-btn"
                    onClick={() => setSearchTerm("")}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              <select
                className="fleetx-status-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                title="Filter by status"
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
              </select>

              <label className="fleetx-live-toggle">
                <input
                  type="checkbox"
                  checked={liveOnly}
                  onChange={(e) => setLiveOnly(e.target.checked)}
                />
                Live only
              </label>

              {!searchTerm && viewMode === "table" && (
                <label className="fleetx-limit-field">
                  Limit
                  <input
                    type="number"
                    min={1}
                    max={6284}
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value) || 1)}
                  />
                </label>
              )}

              <label className="fleetx-live-toggle">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                Auto-refresh (15s)
              </label>

              <div className="fleetx-view-toggle" role="tablist" aria-label="View mode">
                <button
                  className={`fleetx-view-toggle-btn ${
                    viewMode === "table" ? "fleetx-view-toggle-btn--active" : ""
                  }`}
                  onClick={() => setViewMode("table")}
                  role="tab"
                  aria-selected={viewMode === "table"}
                >
                  <List size={14} />
                  Table
                </button>
                <button
                  className={`fleetx-view-toggle-btn ${
                    viewMode === "map" ? "fleetx-view-toggle-btn--active" : ""
                  }`}
                  onClick={() => setViewMode("map")}
                  role="tab"
                  aria-selected={viewMode === "map"}
                >
                  <MapIcon size={14} />
                  Map
                </button>
              </div>

              <button className="fleetx-apply-btn" onClick={() => fetchList()} disabled={loading}>
                <RefreshCw size={15} className={loading ? "spin" : ""} />
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>

            {error && (
              <div className="fleetx-error-banner">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          {!loading && !error && filteredVehicles.length === 0 && (
            <div className="fleetx-empty-state">No vehicles match these filters.</div>
          )}

          {}
          <div className="fleetx-table-area">
            {filteredVehicles.length > 0 && viewMode === "table" && (
              <>
                <div className="fleetx-table-card">
                  <div
                    className="fleetx-table-scroll"
                    ref={tableScrollRef}
                    onScroll={(e) => setHeaderVisible(e.currentTarget.scrollTop < 40)}
                  >
                    <table className="fleetx-data-table">
                      <thead>
                        <tr>
                          <th>Vehicle</th>
                          <th>Make / Model</th>
                          <th>Driver</th>
                          <th>Status</th>
                          <th>Speed</th>
                          <th>Location</th>
                          <th>Last Updated</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedVehicles.map((v) => (
                          <tr
                            key={v.vehicleId}
                            className="fleetx-row-clickable"
                            onClick={() => openDetail(v.vehicleNumber)}
                            title="Click to view full details"
                          >
                            <td>
                              <span className="fleetx-vehicle-badge">{v.vehicleNumber}</span>
                              <span className="fleetx-vehicle-name">{v.vehicleName}</span>
                            </td>
                            <td className="fleetx-muted">
                              {v.vehicleMake} {v.vehicleModel}
                            </td>
                            <td>{v.driverName || "—"}</td>
                            <td>
                              <span
                                className={`fleetx-status-pill fleetx-status-pill--${statusTone(
                                  v.currentStatus
                                )}`}
                              >
                                <span className="fleetx-status-dot" />
                                {statusLabel(v.currentStatus)}
                              </span>
                            </td>
                            <td className="mono">
                              {typeof v.speed === "number" ? `${Math.round(v.speed)} km/h` : "—"}
                            </td>
                            <td className="fleetx-address-cell">{v.address || "—"}</td>
                            <td className="mono">{fmtTimestamp(v.lastUpdatedAt)}</td>
                            <td>
                              <div className="fleetx-row-actions">
                                <button
                                  className="fleetx-track-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTracking(v.vehicleNumber);
                                  }}
                                  title="Track live location"
                                >
                                  <MapPin size={14} />
                                </button>
                                <button
                                  className="fleetx-track-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openHistory(v.vehicleNumber);
                                  }}
                                  title="View trip history"
                                >
                                  <Calendar size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {}
                <div className="fleetx-pagination">
                  <span className="fleetx-pagination-range">
                    Showing <strong>{pageStart}</strong>–<strong>{pageEnd}</strong> of{" "}
                    <strong>{filteredVehicles.length}</strong> vehicles
                  </span>

                  <div className="fleetx-pagination-controls">
                    <button
                      className="fleetx-page-btn"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={14} />
                    </button>

                    <span className="fleetx-pagination-page-label">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      className="fleetx-page-btn"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </>
            )}

            {filteredVehicles.length > 0 && viewMode === "map" && (
              <FleetMap
                vehicles={filteredVehicles}
                onSelectVehicle={openDetail}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>

      {selectedVehicle && (
        <div className="fleetx-modal-overlay" onClick={closeDetail}>
          <div className="fleetx-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fleetx-modal-header">
              <div>
                <h3 className="fleetx-modal-title">
                  {selectedVehicle.vehicleNumber || "Vehicle"}
                </h3>
                {selectedVehicle.vehicleName && (
                  <p className="fleetx-modal-subtitle">{selectedVehicle.vehicleName}</p>
                )}
              </div>
              <button className="fleetx-modal-close-btn" onClick={closeDetail} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {detailLoading && (
              <div className="fleetx-modal-loading">
                <div className="fleetx-spinner" />
                <span>Loading vehicle details…</span>
              </div>
            )}

            {!detailLoading && detailError && (
              <div className="fleetx-modal-error">
                <AlertCircle size={16} />
                <span>{detailError}</span>
              </div>
            )}

            {!detailLoading && !detailError && selectedVehicle.currentStatus && (
              <div className="fleetx-modal-body">
                <div className="fleetx-detail-grid">
                  <DetailField label="Status">
                    <span
                      className={`fleetx-status-pill fleetx-status-pill--${statusTone(
                        selectedVehicle.currentStatus
                      )}`}
                    >
                      <span className="fleetx-status-dot" />
                      {statusLabel(selectedVehicle.currentStatus)}
                    </span>
                  </DetailField>
                  <DetailField label="Make / Model">
                    {selectedVehicle.vehicleMake} {selectedVehicle.vehicleModel}
                  </DetailField>
                  <DetailField label="Fuel Type">{selectedVehicle.fuelType || "—"}</DetailField>
                  <DetailField label="Driver">{selectedVehicle.driverName || "—"}</DetailField>
                  <DetailField label="Driver Number">
                    {selectedVehicle.driverNumber || "—"}
                  </DetailField>
                  <DetailField label="Speed">
                    {typeof selectedVehicle.speed === "number"
                      ? `${Math.round(selectedVehicle.speed)} km/h`
                      : "—"}
                  </DetailField>
                  <DetailField label="Odometer">
                    {selectedVehicle.totalOdometer
                      ? `${Math.round(selectedVehicle.totalOdometer)} km`
                      : "—"}
                  </DetailField>
                  <DetailField label="Last Updated">
                    {fmtTimestamp(selectedVehicle.lastUpdatedAt)}
                  </DetailField>
                </div>

                <div className="fleetx-detail-location">
                  <MapPin size={14} />
                  <span>{selectedVehicle.address || "Address unavailable"}</span>
                </div>

                {typeof selectedVehicle.latitude === "number" && (
                  <div className="fleetx-detail-coords">
                    <Gauge size={13} />
                    {selectedVehicle.latitude.toFixed(5)}, {selectedVehicle.longitude.toFixed(5)}
                  </div>
                )}

                <div className="fleetx-detail-actions">
                  <button
                    className="fleetx-detail-track-btn"
                    onClick={() => {
                      const num = selectedVehicle.vehicleNumber;
                      closeDetail();
                      openTracking(num);
                    }}
                  >
                    <MapPin size={14} />
                    Track live on map
                  </button>
                  <button
                    className="fleetx-detail-track-btn"
                    onClick={() => {
                      const num = selectedVehicle.vehicleNumber;
                      closeDetail();
                      openHistory(num);
                    }}
                  >
                    <Calendar size={14} />
                    View trip history
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {trackingVehicleNumber && (
        <VehicleLiveMap
          vehicleNumber={trackingVehicleNumber}
          apiBaseUrl={apiBaseUrl}
          getToken={getToken}
          onClose={closeTracking}
        />
      )}

      {historyVehicleNumber && (
        <TripHistoryModal
          vehicleNumber={historyVehicleNumber}
          apiBaseUrl={apiBaseUrl}
          getToken={getToken}
          onClose={closeHistory}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "neutral" }) {
  return (
    <div className={`fleetx-stat-card fleetx-stat-card--${tone}`}>
      <span className="fleetx-stat-icon">
        <Icon size={23} strokeWidth={1.9} />
      </span>
      <span className="fleetx-stat-copy">
        <span className="fleetx-stat-value">{value ?? "—"}</span>
        <span className="fleetx-stat-label">{label}</span>
      </span>
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div className="fleetx-detail-field">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}
